import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSmoothScroll } from '@/hooks/useSmoothScroll';
import { useTranslation } from 'react-i18next';
import gSoftLogoBeyaz from '@/pages/images/g-soft-logo-beyaz.png';
import { useSpecialDayLogo } from '@/hooks/useSpecialDayLogo';

interface NavLink {
  label: string;
  href: string;
  isRoute?: boolean;
}

interface SiteNavbarProps {
  links?: NavLink[];
  sectionAware?: boolean;
}

const HOME_LINKS: NavLink[] = [
  { label: 'nav.home',    href: '#' },
  { label: 'nav.apps',    href: '/apps',  isRoute: true },
  { label: 'nav.games',   href: '/games', isRoute: true },
  { label: 'nav.stack',   href: '#stack' },
  { label: 'nav.contact', href: '#contact' },
];
const WEBSITE_OFFER_LINK: NavLink = {
  label: 'nav.websiteOffer',
  href: '/web-site-mi-istiyorsunuz',
  isRoute: true,
};

function ScrollProgress() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const update = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(docHeight > 0 ? (scrollTop / docHeight) * 100 : 0);
    };
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, []);
  return (
    <div className="absolute bottom-0 left-0 right-0 h-px bg-white/5">
      <div className="h-full bg-white/40 transition-all duration-100" style={{ width: `${progress}%` }} />
    </div>
  );
}

function LanguageSwitcher() {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const currentLanguageCode = (i18n.resolvedLanguage || i18n.language || 'tr').split('-')[0];

  const languages = [
    { code: 'tr', label: t('lang.tr') },
    { code: 'en', label: t('lang.en') },
  ];

  const currentLang = languages.find((l) => l.code === currentLanguageCode) || languages[0];

  const handleSelect = (code: string) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-1.5 border border-white/10 hover:border-white/25 text-neutral-500 hover:text-white transition-all duration-200 cursor-pointer"
      >
        <span className="font-mono text-[10px] tracking-widest uppercase">{currentLang.code}</span>
        <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
          />
          <div className="absolute right-0 top-full mt-1 bg-[#111] border border-white/10 z-50 min-w-[100px]">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleSelect(lang.code)}
                className={`w-full px-4 py-2 text-left font-mono text-[10px] tracking-wider uppercase transition-colors duration-200 cursor-pointer ${
                  lang.code === currentLanguageCode
                    ? 'text-white bg-white/5'
                    : 'text-neutral-400 hover:text-neutral-200 hover:bg-white/[0.02]'
                }`}
              >
                {lang.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function SiteNavbar({ links, sectionAware = false }: SiteNavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [time, setTime] = useState('');
  const [activeSection, setActiveSection] = useState('home');
  const navigate = useNavigate();
  const location = useLocation();
  const { scrollTo } = useSmoothScroll();
  const { t, i18n } = useTranslation();
  const brandLogo = useSpecialDayLogo(gSoftLogoBeyaz);

  const baseLinks = links ?? HOME_LINKS;
  const navLinks = baseLinks.some((l) => l.href === WEBSITE_OFFER_LINK.href)
    ? baseLinks
    : [...baseLinks, WEBSITE_OFFER_LINK];

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const langCode = (i18n.resolvedLanguage || i18n.language || 'tr').split('-')[0];
      const locale = langCode === 'en' ? 'en-US' : 'tr-TR';
      setTime(now.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [i18n.language, i18n.resolvedLanguage]);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
      if (!sectionAware) return;
      const ids = ['contact', 'stack', 'products'];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= window.innerHeight * 0.4) {
          setActiveSection(id);
          return;
        }
      }
      setActiveSection('home');
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [sectionAware]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  const handleLinkClick = (link: NavLink, e: React.MouseEvent) => {
    e.preventDefault();
    setMenuOpen(false);

    if (link.isRoute) {
      navigate(link.href);
      return;
    }

    if (link.href.startsWith('#')) {
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => scrollTo(link.href), 100);
      } else {
        scrollTo(link.href);
      }
      return;
    }

    navigate(link.href);
  };

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setMenuOpen(false);

    if (location.pathname !== '/') {
      navigate('/');
      return;
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-[#0c0c0c]/95 backdrop-blur-xl border-b border-white/8'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-6 lg:px-14 h-16 flex items-center justify-between">
          {/* Logo */}
          <a
            href="/"
            onClick={handleLogoClick}
            className="flex items-center gap-3 group cursor-pointer"
          >
            <img
              src={brandLogo}
              alt="G-Software logo"
              className="w-10 h-10 object-contain"
            />
            <span className="text-white font-mono text-sm tracking-widest uppercase">G-Software</span>
          </a>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((l) => {
              const sectionKey = l.href.replace('#', '') || 'home';
              const isActive = sectionAware
                ? activeSection === sectionKey
                : location.pathname === l.href;
              return (
                <a
                  key={l.label}
                  href={l.href}
                  onClick={(e) => handleLinkClick(l, e)}
                  className={`relative flex items-center gap-2 px-4 py-2 font-mono text-xs tracking-widest uppercase transition-colors duration-200 cursor-pointer ${
                    isActive
                      ? 'text-white'
                      : 'text-neutral-400 hover:text-neutral-300'
                  }`}
                >
                  <span
                    className={`w-1 h-1 rounded-full transition-all duration-300 ${
                      isActive ? 'bg-white opacity-100 scale-100' : 'bg-transparent opacity-0 scale-0'
                    }`}
                  />
                  {t(l.label)}
                  <span
                    className={`absolute bottom-0 left-4 right-4 h-px bg-white transition-all duration-300 origin-left ${
                      isActive ? 'scale-x-100 opacity-100' : 'scale-x-0 opacity-0'
                    }`}
                  />
                </a>
              );
            })}
          </nav>

          {/* Right */}
          <div className="hidden md:flex items-center gap-4">
            <LanguageSwitcher />
            <div className="flex items-center gap-2 border-white/8 border px-3 py-1.5">
              <span className="font-mono text-[10px] text-neutral-700 tracking-widest uppercase">
                {sectionAware ? `/${activeSection}` : location.pathname}
              </span>
            </div>
            <span className="font-mono text-xs text-neutral-400 tabular-nums">{time}</span>
          </div>

          {/* Mobile hamburger */}
          <div className="flex md:hidden items-center gap-3">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="text-white"
              aria-label="Toggle menu"
            >
              <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
            </button>
          </div>
        </div>
        <ScrollProgress />
      </header>

      {menuOpen && (
        <div className="fixed inset-0 z-40 bg-[#0c0c0c] flex flex-col items-center justify-center gap-8 px-6 pt-24 pb-20 overflow-y-auto">
          {navLinks.map((l) => (
            <a
              key={l.label}
              href={l.href}
              onClick={(e) => handleLinkClick(l, e)}
              className="font-mono text-2xl sm:text-3xl text-center text-neutral-400 hover:text-white tracking-widest uppercase transition-colors duration-200 cursor-pointer"
            >
              {t(l.label)}
            </a>
          ))}
          <div className="flex items-center gap-4 mt-6">
            <LanguageSwitcher />
          </div>
          <div className="absolute bottom-10 font-mono text-xs text-neutral-700 tracking-widest uppercase">
            {location.pathname}
          </div>
        </div>
      )}
    </>
  );
}

